import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/clients/supabase';
import { useToast } from '../../../../shared/contexts/ToastContext';
import { ResendResetLink } from './ResendResetLink';

export const ResetPasswordForm = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    // ✅ PERBAIKAN: Validasi session yang lebih robust dengan retry mechanism
    const validateResetSession = async () => {
      try {
        let retryCount = 0;
        const maxRetries = 30; // 15 detik total (500ms x 30)
        
        const checkSession = async () => {
          try {
            const { data: { session }, error } = await supabase.auth.getSession();
            
            if (error) {
              console.error('❌ Error getting session:', error);
              // Don't retry on auth errors, go to alternatives
              setShowAlternatives(true);
              return;
            }
            
            if (session && session.user) {
              console.log('✅ Valid recovery session found for user:', session.user.email);
              
              // Store email info for form
              const urlParams = new URLSearchParams(window.location.search);
              const emailFromUrl = urlParams.get('email');
              const emailFromSession = session.user.email;
              const emailFromStorage = localStorage.getItem('reset_password_email');
              
              const email = emailFromUrl || emailFromSession || emailFromStorage;
              if (email && email !== localStorage.getItem('reset_password_email')) {
                localStorage.setItem('reset_password_email', email);
                localStorage.setItem('reset_password_timestamp', Date.now().toString());
              }
              
              setIsValidSession(true);
              return;
            }
            
            // No session yet, continue retrying
            retryCount++;
            if (retryCount < maxRetries) {
              console.log(`⏳ Waiting for recovery session... (${retryCount}/${maxRetries})`);
              setTimeout(checkSession, 500);
            } else {
              // After maximum retries, check stored info as last resort
              console.warn('⚠️ No session after maximum retries, checking stored info...');
              
              const storedEmail = localStorage.getItem('reset_password_email');
              const storedTimestamp = localStorage.getItem('reset_password_timestamp');
              
              if (storedEmail && storedTimestamp) {
                const timeDiff = Date.now() - parseInt(storedTimestamp);
                const minutesDiff = timeDiff / (1000 * 60);
                
                if (minutesDiff < 60) { // Allow 1 hour grace period
                  console.log('✅ Using stored reset info as fallback (within 1 hour)');
                  setIsValidSession(true);
                  return;
                }
              }
              
              // No valid session or stored info found
              console.error('❌ No valid reset session found after all retries');
              showToast({ 
                type: 'error', 
                message: 'Sesi reset tidak valid. Link mungkin sudah kadaluarsa atau sudah digunakan.' 
              });
              setShowAlternatives(true);
            }
          } catch (sessionError) {
            console.error('❌ Exception during session check:', sessionError);
            retryCount++;
            if (retryCount < maxRetries) {
              setTimeout(checkSession, 500);
            } else {
              setShowAlternatives(true);
            }
          }
        };
        
        // Start checking session
        await checkSession();
        
      } catch (error) {
        console.error('❌ Error in validateResetSession:', error);
        showToast({ 
          type: 'error', 
          message: 'Terjadi kesalahan saat memvalidasi sesi reset. Silakan coba lagi.' 
        });
        setTimeout(() => navigate('/forgot-password'), 2000);
      }
    };

    validateResetSession();
  }, [navigate, showToast]);

  const validatePassword = (password: string): boolean => {
    if (password.length < 8) {
      showToast({ type: 'error', message: 'Password minimal 8 karakter' });
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      showToast({ type: 'error', message: 'Password harus mengandung minimal 1 huruf besar' });
      return false;
    }
    if (!/[a-z]/.test(password)) {
      showToast({ type: 'error', message: 'Password harus mengandung minimal 1 huruf kecil' });
      return false;
    }
    if (!/[0-9]/.test(password)) {
      showToast({ type: 'error', message: 'Password harus mengandung minimal 1 angka' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      showToast({ type: 'error', message: 'Silakan isi semua field' });
      return;
    }

    if (newPassword !== confirmPassword) {
      showToast({ type: 'error', message: 'Password tidak cocok' });
      return;
    }

    if (!validatePassword(newPassword)) {
      return;
    }

    setLoading(true);

    try {
      // ✅ PERBAIKAN: Validasi session lebih ketat sebelum update
      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
      
      console.log('🔍 Current session before password update:', {
        hasSession: !!currentSession,
        userId: currentSession?.user?.id,
        email: currentSession?.user?.email,
        accessToken: currentSession?.access_token ? 'present' : 'missing',
        sessionError: sessionError?.message
      });

      if (sessionError) {
        console.error('❌ Session error:', sessionError);
        showToast({ 
          type: 'error', 
          message: 'Sesi reset bermasalah. Silakan minta link reset baru.' 
        });
        setTimeout(() => navigate('/forgot-password'), 2000);
        return;
      }

      if (!currentSession || !currentSession.user) {
        console.error('❌ No valid session for password update');
        showToast({ 
          type: 'error', 
          message: 'Sesi reset tidak valid. Silakan minta link reset baru.' 
        });
        setTimeout(() => navigate('/forgot-password'), 2000);
        return;
      }

      // Proceed with password update
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      console.log('🔍 Password update result:', { 
        success: !updateError, 
        error: updateError?.message 
      });

      if (updateError) {
        console.error('❌ Password update failed:', updateError);
        
        if (updateError.message.includes('session') || updateError.message.includes('Auth session missing')) {
          showToast({ 
            type: 'error', 
            message: 'Sesi reset telah berakhir. Silakan minta link reset baru.' 
          });
          setTimeout(() => navigate('/forgot-password'), 2000);
        } else if (updateError.message.includes('Password')) {
          showToast({ 
            type: 'error', 
            message: 'Password tidak memenuhi kriteria keamanan. Silakan coba password yang lebih kuat.' 
          });
        } else {
          showToast({ 
            type: 'error', 
            message: `Gagal update password: ${updateError.message}` 
          });
        }
      } else {
        console.log('✅ Password updated successfully');
        
        // Clear stored reset info on successful password update
        localStorage.removeItem('reset_password_email');
        localStorage.removeItem('reset_password_timestamp');
        
        // Sign out the recovery session after successful password reset
        try {
          await supabase.auth.signOut();
          console.log('✅ Recovery session signed out');
        } catch (signOutError) {
          console.warn('⚠️ Could not sign out recovery session:', signOutError);
          // Continue anyway, password was updated successfully
        }
        
        showToast({ 
          type: 'success', 
          message: 'Password berhasil diperbarui! Silakan login dengan password baru Anda.' 
        });
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (err) {
      console.error('❌ Exception during password update:', err);
      showToast({ 
        type: 'error', 
        message: 'Terjadi kesalahan saat reset password. Silakan coba lagi.' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isValidSession) {
    if (showAlternatives) {
      return (
        <div className="w-full max-w-md space-y-6">
          <div className="card p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Link Reset Tidak Valid
            </h3>
            <p className="text-gray-600 mb-6">
              Link reset password sudah kadaluarsa atau sudah digunakan. Pilih salah satu opsi di bawah:
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/forgot-password')}
                className="btn-primary w-full"
              >
                Minta Link Reset Baru
              </button>
              

              
              <button
                onClick={() => window.location.reload()}
                className="btn-outline w-full"
              >
                Coba Lagi
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="w-full max-w-md space-y-6">
        <div className="card p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Memverifikasi Sesi Reset
          </h3>
          <p className="text-gray-600 mb-2">Sedang memvalidasi link reset password...</p>
          <p className="text-xs text-gray-500">
            Proses ini memastikan keamanan akun Anda. Mohon tunggu sebentar.
          </p>
          
          {/* Progress indicator */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-primary-600 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Validasi keamanan sedang berlangsung...</p>
          </div>
        </div>
        
        {/* Show resend option if validation takes too long */}
        <ResendResetLink 
          email={new URLSearchParams(window.location.search).get('email') || undefined}
          onSuccess={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-md">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Reset Password
        </h2>
        <p className="text-gray-600">
          Masukkan password baru Anda
        </p>
      </div>

      {/* Form Card */}
      <div className="card p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* New Password Input */}
          <div>
            <label htmlFor="newPassword" className="label">
              Password Baru
            </label>
            <div className="relative">
              <input
                id="newPassword"
                name="newPassword"
                type={showPassword ? 'text' : 'password'}
                required
                className="input pr-10"
                placeholder="Masukkan password baru"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Minimal 8 karakter, mengandung huruf besar, huruf kecil, dan angka
            </p>
          </div>

          {/* Confirm Password Input */}
          <div>
            <label htmlFor="confirmPassword" className="label">
              Konfirmasi Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                required
                className="input pr-10"
                placeholder="Masukkan ulang password baru"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showConfirmPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn-primary w-full"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Memperbarui...
              </span>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};
